import { Alert, Box, Typography } from '@mui/material';
import { ChevronRight, CircleCheck } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-admin';

import type { AdminAttention, AdminAttentionItem } from '@/admin/adminApi';
import { Badge, Button, Card, EmptyState, Icon } from '@/ui/kit';
import type { ColorTokens } from '@/ui/tokens';

import { describeAttention, entryPath, SEVERITY_LABELS, SEVERITY_TONES, sourceLabel } from './dashboardModel';
import { ReadBlock } from './ReadBlock';
import type { AdminRead } from './useAdminRead';

/**
 * « À traiter » : ce qui attend une action, dans l'ordre du serveur.
 *
 * - **Le classement est celui du serveur** (gravite, puis nombre) : l'ecran ne
 *   retrie pas, il ne decide pas de ce qui est urgent.
 * - **Rien a traiter est une liste vide**, dite avec une coche — jamais une ligne
 *   a zero. Et **une source indisponible n'est pas « rien »** : un avis le dit
 *   dans la carte, car ne pas savoir n'est pas aller bien.
 * - **La gravite est ecrite** (« Critique », « À surveiller ») : la teinte
 *   n'est qu'un renfort.
 * - **Une ligne mene a l'ecran qui permet d'agir**, **seulement si cet ecran
 *   existe** (registre de navigation). Aucun rejeu depuis le tableau de bord.
 *   Sous 600 px la ligne entiere est le lien (cible de 44 px au moins).
 */

/** Couple aplat / encre de la pastille d'icone, par gravite (les memes que `Badge`). */
const SEVERITY_ICON: Readonly<Record<AdminAttentionItem['severity'], { readonly fill: keyof ColorTokens; readonly ink: keyof ColorTokens }>> = {
  critical: { fill: 'criticalSoft', ink: 'criticalText' },
  warning: { fill: 'accentYellowSoft', ink: 'accentYellowStrong' },
  info: { fill: 'infoSoft', ink: 'infoText' },
};

const Row = ({ item, narrow }: { readonly item: AdminAttentionItem; readonly narrow: boolean }): ReactElement => {
  const line = describeAttention(item);
  const path = entryPath(line.ownerEntryId);
  const tone = SEVERITY_TONES[item.severity];

  const content = (
    <>
      <Box
        aria-hidden
        sx={{ display: 'inline-flex', p: 1, borderRadius: '10px', flexShrink: 0, bgcolor: `tokens.${SEVERITY_ICON[item.severity].fill}`, color: `tokens.${SEVERITY_ICON[item.severity].ink}` }}
      >
        <Icon icon={line.icon} size={18} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="p" sx={{ fontSize: 14, fontWeight: 700, lineHeight: '20px' }}>
          {line.title}
        </Typography>
        {line.detail === null ? null : (
          <Typography component="p" sx={{ fontSize: 12, lineHeight: '16px', color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
            {line.detail}
          </Typography>
        )}
      </Box>
      <Badge label={SEVERITY_LABELS[item.severity]} tone={tone} />
    </>
  );

  const frame = { display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, borderBottom: 1, borderColor: 'tokens.border' } as const;

  if (narrow && path !== null) {
    return (
      <Box component="li" sx={{ listStyle: 'none' }}>
        <Box
          component={Link}
          to={path}
          aria-label={`${line.title} : ouvrir`}
          sx={{ ...frame, minHeight: 44, color: 'inherit', textDecoration: 'none', '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: 2 } }}
        >
          {content}
          <Icon icon={ChevronRight} size={18} />
        </Box>
      </Box>
    );
  }

  return (
    <Box component="li" sx={{ ...frame, listStyle: 'none' }}>
      {content}
      {path === null ? null : (
        <Button size="S" to={path} aria-label={`Ouvrir : ${line.title}`}>
          Ouvrir
        </Button>
      )}
    </Box>
  );
};

export const AttentionCard = ({
  attention,
  narrow,
  onRetry,
}: {
  readonly attention: AdminRead<AdminAttention>;
  readonly narrow: boolean;
  readonly onRetry: () => void;
}): ReactElement => (
  <Card
    title="À traiter"
    subtitle="Classé par urgence"
    actions={
      attention.kind === 'ready' && attention.data.items.length > 0 ? (
        <Badge
          label={attention.data.items.length}
          tone={attention.data.items.some((item) => item.severity === 'critical') ? 'danger' : 'warning'}
        />
      ) : undefined
    }
  >
    <ReadBlock state={attention} label="À traiter" onRetry={onRetry} skeletonHeight={narrow ? 160 : 220}>
      {({ items, unavailableSources }) => (
        <>
          {unavailableSources.length === 0 ? null : (
            <Alert severity="warning" variant="outlined">
              {`Sources non lues : ${unavailableSources.map(sourceLabel).join(', ')}. Ce n’est pas « rien à traiter » : l’état de ces sources est inconnu.`}
            </Alert>
          )}
          {items.length === 0 ? (
            unavailableSources.length === 0 ? (
              <EmptyState icon={CircleCheck} title="Rien à traiter" description="Aucun traitement en erreur, aucune alerte de santé, aucune maintenance en cours." />
            ) : null
          ) : (
            <Box component="ul" sx={{ m: 0, p: 0 }}>
              {items.map((item) => (
                <Row key={item.kind} item={item} narrow={narrow} />
              ))}
            </Box>
          )}
        </>
      )}
    </ReadBlock>
  </Card>
);
